import { Box, Button, ButtonGroup, Tooltip } from "@mui/material";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PrintIcon from '@mui/icons-material/Print';
import * as XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function ExportButtons({ data, columns, filename = "reporte", title = "Reporte" }) {

    // Exportar a Excel
    const exportToExcel = () => {
        if (!data || data.length === 0) {
            alert("No hay datos para exportar");
            return;
        }

        // Preparar datos para Excel
        const excelData = data.map(row => {
            const newRow = {};
            columns.forEach(col => {
                newRow[col.header] = row[col.field];
            });
            return newRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

        // Ajustar ancho de columnas
        const colWidths = columns.map(col => ({ wch: Math.max(col.header.length, 15) }));
        worksheet['!cols'] = colWidths;

        // Generar archivo
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `${filename}_${getDateString()}.xlsx`);
    };

    // Exportar a PDF
    const exportToPDF = () => {
        if (!data || data.length === 0) {
            alert("No hay datos para exportar");
            return;
        }

        const doc = new jsPDF();

        // Titulo
        doc.setFontSize(16);
        doc.setTextColor(30, 58, 95); // Color corporativo
        doc.text(title, 14, 20);

        // Fecha de generacion
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generado: ${new Date().toLocaleString()}`, 14, 28);

        // Preparar datos para la tabla
        const headers = columns.map(col => col.header);
        const rows = data.map(row => columns.map(col => {
            const value = row[col.field];
            if (col.type === 'money') {
                return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 });
            }
            return value;
        }));

        // Generar tabla
        autoTable(doc, {
            head: [headers],
            body: rows,
            startY: 35,
            theme: 'grid',
            headStyles: {
                fillColor: [30, 58, 95],
                textColor: [255, 255, 255],
                fontStyle: 'bold'
            },
            alternateRowStyles: {
                fillColor: [245, 247, 250]
            },
            styles: {
                fontSize: 9,
                cellPadding: 3
            },
            columnStyles: columns.reduce((acc, col, index) => {
                if (col.type === 'money') {
                    acc[index] = { halign: 'right' };
                }
                return acc;
            }, {})
        });

        // Pie de pagina
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text(
                `Pagina ${i} de ${pageCount}`,
                doc.internal.pageSize.width / 2,
                doc.internal.pageSize.height - 10,
                { align: 'center' }
            );
        }

        doc.save(`${filename}_${getDateString()}.pdf`);
    };

    // Imprimir
    const handlePrint = () => {
        if (!data || data.length === 0) {
            alert("No hay datos para imprimir");
            return;
        }

        // Crear contenido HTML para imprimir
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>${title}</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 20px;
                        color: #333;
                    }
                    h1 {
                        color: #1e3a5f;
                        border-bottom: 3px solid #d4af37;
                        padding-bottom: 10px;
                        margin-bottom: 5px;
                    }
                    .fecha {
                        color: #666;
                        font-size: 12px;
                        margin-bottom: 20px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 20px;
                    }
                    th {
                        background-color: #1e3a5f;
                        color: white;
                        padding: 12px 8px;
                        text-align: left;
                        font-weight: bold;
                    }
                    td {
                        padding: 10px 8px;
                        border-bottom: 1px solid #ddd;
                    }
                    tr:nth-child(even) {
                        background-color: #f5f7fa;
                    }
                    tr:hover {
                        background-color: #e8f0fe;
                    }
                    .money {
                        text-align: right;
                    }
                    .total-row {
                        font-weight: bold;
                        background-color: #1e3a5f !important;
                        color: white;
                    }
                    @media print {
                        body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                    }
                </style>
            </head>
            <body>
                <h1>${title}</h1>
                <div class="fecha">Generado: ${new Date().toLocaleString()}</div>
                <table>
                    <thead>
                        <tr>
                            ${columns.map(col => `<th>${col.header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map(row => `
                            <tr>
                                ${columns.map(col => {
                                    const value = row[col.field];
                                    const formattedValue = col.type === 'money'
                                        ? parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })
                                        : value;
                                    return `<td class="${col.type === 'money' ? 'money' : ''}">${formattedValue}</td>`;
                                }).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <p style="margin-top: 20px; color: #666; font-size: 12px;">
                    Total de registros: ${data.length}
                </p>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    const getDateString = () => {
        const now = new Date();
        return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
            <ButtonGroup variant="outlined" size="small">
                <Tooltip title="Exportar a Excel">
                    <Button
                        onClick={exportToExcel}
                        startIcon={<FileDownloadIcon />}
                        sx={{
                            color: '#1e3a5f',
                            borderColor: '#1e3a5f',
                            '&:hover': {
                                borderColor: '#d4af37',
                                backgroundColor: 'rgba(212, 175, 55, 0.1)'
                            }
                        }}
                    >
                        Excel
                    </Button>
                </Tooltip>
                <Tooltip title="Exportar a PDF">
                    <Button
                        onClick={exportToPDF}
                        startIcon={<PictureAsPdfIcon />}
                        sx={{
                            color: '#1e3a5f',
                            borderColor: '#1e3a5f',
                            '&:hover': {
                                borderColor: '#d4af37',
                                backgroundColor: 'rgba(212, 175, 55, 0.1)'
                            }
                        }}
                    >
                        PDF
                    </Button>
                </Tooltip>
                <Tooltip title="Imprimir">
                    <Button
                        onClick={handlePrint}
                        startIcon={<PrintIcon />}
                        sx={{
                            color: '#1e3a5f',
                            borderColor: '#1e3a5f',
                            '&:hover': {
                                borderColor: '#d4af37',
                                backgroundColor: 'rgba(212, 175, 55, 0.1)'
                            }
                        }}
                    >
                        Imprimir
                    </Button>
                </Tooltip>
            </ButtonGroup>
        </Box>
    );
}
